<script lang="ts" setup>
import {
  UIAvatar,
  UITag,
  getColorFromString,
  UITextXsRegular,
  UIPopover,
  UITextXsMedium,
} from '@gohighlevel/ghl-ui'
import { InfoCircleIcon } from '@gohighlevel/ghl-icons/24/outline'
import { useAppStore } from '@/store/app'
import { PropType } from 'vue'
import {
  IRecipient,
  RecipientRolesEnum,
  useTypography,
  recipientThumbnail,
  DOCUMENT_SENDER_ID,
} from '@gohighlevel/ghl-proposals-common'
import { usePostmate } from '@/composition/usePostmate'
import { useRoute } from 'vue-router'

const { toCapitalize } = useTypography()
const { redirectOnParentRoute } = usePostmate()
const appStore = useAppStore()
const route = useRoute()

const props = defineProps({
  recipient: { type: Object as PropType<IRecipient> },
  showRecipientTag: { type: Boolean, default: true },
  showSenderInfo: { type: Boolean, default: false },
})

const onClickCustomer = () => {
  if (props.recipient?.id) {
    const contactId = props.recipient?.id
    redirectOnParentRoute(
      {
        name: 'contact_detail-v2',
        params: {
          locationId: route.params.locationId as string,
          contactId: contactId,
        },
      },
      { newTab: true }
    )
  }
}
</script>

<template>
  <div class="flex" v-if="recipient?.id">
    <div class="flex p-2 cursor-pointer">
      <div class="w-10 mr-4">
        <UIAvatar
          :style="{
            backgroundColor: getColorFromString(
              toCapitalize(
                `${recipient?.contactName}` ||
                  `${recipient.firstName} ${recipient.lastName}`
              ).trim()
            ),
          }"
        >
          {{ recipientThumbnail(recipient.firstName, recipient.lastName) }}
        </UIAvatar>
      </div>
      <div class="flex flex-col">
        <div class="text-sm w-40">
          <div class="text-gray-700 font-semibold truncate capitalize">
            <a
              v-if="recipient.entityName === 'contacts'"
              class="cursor-pointer hover:text-blue-600"
              @click.prevent="onClickCustomer()"
            >
              {{ recipient.firstName }} {{ recipient.lastName }}
              <span v-if="appStore.userId === recipient.id" class="ml-1">
                {{ $t('common.you') }}
              </span>
            </a>
            <div v-else class="w-full flex items-center">
              <a class="pointer-events-none">
                {{ recipient.firstName }} {{ recipient.lastName }}
                <span v-if="appStore.userId === recipient.id" class="ml-1">
                  {{ $t('common.you') }}
                </span>
              </a>
              <span
                class="ml-1"
                v-if="
                  recipient?.id &&
                  props.showSenderInfo &&
                  recipient.id === DOCUMENT_SENDER_ID
                "
              >
                <UIPopover
                  placement="left"
                  to="body"
                  :width="150"
                  :animated="true"
                >
                  <template #trigger>
                    <InfoCircleIcon class="ml-1 mt-1 h-4 w-4 text-gray-500" />
                  </template>
                  <UITextXsMedium>{{ $t('common.senderInfo') }}</UITextXsMedium>
                </UIPopover>
              </span>
            </div>
          </div>
        </div>
        <div class="flex items-end text-gray-500 font-light w-40">
          <UIPopover
            trigger="hover"
            content-style="background-color: rgb(3 7 18)"
            arrow-style="background-color: rgb(3 7 18)"
            style="padding: 0"
          >
            <template #trigger>
              <div>
                <UITextXsRegular class="truncate w-40">
                  {{ recipient.email }}
                </UITextXsRegular>
                <UITextXsRegular v-if="recipient.phone" class="truncate w-40">
                  {{ recipient.phone }}
                </UITextXsRegular>
              </div>
            </template>
            <div class="font-light text-neutral-50 p-2">
              <UITextXsRegular class="capitalize">
                {{ recipient.firstName }} {{ recipient.lastName }}
                <span v-if="appStore.userId === recipient.id" class="ml-1">
                  {{ $t('common.you') }}
                </span>
              </UITextXsRegular>
              <UITextXsRegular>
                {{ recipient.email }}
              </UITextXsRegular>
              <UITextXsRegular v-if="recipient.phone">
                {{ recipient.phone }}
              </UITextXsRegular>
            </div>
          </UIPopover>
        </div>
        <div class="flex gap-2 mt-2" v-if="showRecipientTag">
          <UITag
            v-if="recipient.isPrimary"
            type="success"
            size="small"
            class="text-[8px]"
          >
            {{ $t('common.primary') }}
          </UITag>
          <div v-if="recipient?.role" class="grow">
            <UITag
              class="text-[8px]"
              size="small"
              v-if="recipient?.role === RecipientRolesEnum.USER"
              type="warning"
            >
              {{ $t('common.cc') }}
            </UITag>
            <UITag
              class="text-[8px]"
              size="small"
              v-if="recipient?.role === RecipientRolesEnum.SIGNER"
              :type="recipient.imgUrl ? 'success' : 'error'"
            >
              {{ recipient.imgUrl ? $t('common.signed') : $t('common.signer') }}
            </UITag>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
